import React, { useContext, useEffect, useRef } from 'react'
import { DContext } from '../../context/DataContext'
import { useLocation, useNavigate } from 'react-router-dom'
import LoadingPage from './Loading'
import SearchBlock from '../blocks/SearchBlock'
import { useTranslation } from 'react-i18next'
import MetaData from '../../utils/MetaData'
import { FiChevronRight, FiRotateCcw } from 'react-icons/fi'

const Packages = () => {

  const { t } = useTranslation()

  const { filteredPackages, setRenderDropdown, setEnteredDestination, setEnteredActivity, setEnteredText, enteredActivity, enteredDestination, enteredText, renderDropdown, currency, filterPackages } = useContext(DContext)
  const navPage = useNavigate()
  const location = useLocation()
  const cardsBlockRef = useRef(null)
  const routeSyncKeyRef = useRef('')
  console.log('filteredPackages', filteredPackages)

  // const handleBucketList = (e, packageId) => {
  //   e.stopPropagation()
  //   const isExist = cart.some(item=>item===packageId)
  //   if(!isExist){
  //     setCart(prev=>{
  //       let newCart = [...prev]
  //       newCart.push(packageId)
  //       return newCart
  //     }) 
  //   }
  //   navPage('/bucket-list')
  // }

  useEffect(() => {
    const routeKey = `${location.pathname}?${location.search}`
    if (routeSyncKeyRef.current === routeKey) {
      return
    }
    routeSyncKeyRef.current = routeKey

    const params = new URLSearchParams(location.search)
    const destination = (params.get('destination') || '').trim()
    const activity = (params.get('activity') || '').trim()
    const packageQuery = (params.get('package') || '').trim()

    setRenderDropdown(null)

    if (destination) {
      setEnteredDestination(destination)
      setEnteredActivity('')
      setEnteredText('')
      filterPackages(destination, 'destination')
    }
    else if (activity) {
      setEnteredActivity(activity)
      setEnteredDestination('')
      setEnteredText('')
      filterPackages(activity, 'activity')
    }
    else if (packageQuery) {
      setEnteredText(packageQuery)
      setEnteredDestination('')
      setEnteredActivity('')
      filterPackages(packageQuery, 'package')
    }
    else {
      setEnteredDestination('')
      setEnteredActivity('')
      setEnteredText('')
      filterPackages(null, 'all')
    }

    const hasSearchQuery = destination || activity || packageQuery
    if (hasSearchQuery && cardsBlockRef.current) {
      requestAnimationFrame(() => {
        const top = cardsBlockRef.current.getBoundingClientRect().top + window.pageYOffset
        const stickyHeaderOffset = window.innerWidth <= 768 ? 0 : 120
        window.scrollTo({
          top: Math.max(top - stickyHeaderOffset, 0),
          behavior: 'smooth'
        })
      })
    }
    else {
      const container = document.getElementById('container-element')
      if (container) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [location.pathname, location.search, filterPackages, setEnteredActivity, setEnteredDestination, setEnteredText, setRenderDropdown])

  if (!filteredPackages || filteredPackages === null) {
    return <LoadingPage />
  }

  const siteUrl = window.location.origin
  const pageUrl = `${siteUrl}/packages`
  const canonicalUrl = `${siteUrl}/packages`
  const queryLabel = enteredDestination || enteredActivity || enteredText
  const queryContext = queryLabel ? ` for ${queryLabel}` : ''
  const listItems = filteredPackages.slice(0, 25).map((pkg, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: `${siteUrl}/package/${pkg.path}`,
    name: pkg.title
  }))

  const packagesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Krabi Tour Packages${queryContext}`,
    description: `Discover island hopping, snorkeling, diving, and adventure tour packages in Krabi, Thailand${queryContext}.`,
    url: pageUrl,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: listItems
    }
  }

  const resetSearch = () => {
    setRenderDropdown(null)
    setEnteredDestination('')
    setEnteredActivity('')
    setEnteredText('')
    filterPackages(null, 'all')
    if (location.search) {
      navPage('/packages', { replace: true })
    }
  }

  const getCardScrollOffset = () => (window.innerWidth <= 768 ? 0 : 120)

  const scrollToCardBlock = () => {
    if (!cardsBlockRef.current) return

    const top = cardsBlockRef.current.getBoundingClientRect().top + window.pageYOffset
    const stickyHeaderOffset = getCardScrollOffset()
    window.scrollTo({
      top: Math.max(top - stickyHeaderOffset, 0),
      behavior: 'smooth'
    })
  }

  const isFiltered = () => {
    if (renderDropdown || enteredDestination !== '' || enteredActivity !== '' || enteredText !== '') {
      return false
    }
    else {
      return true
    }
  }

  const renderPrice = (pkg) => {
    if (pkg.priceType === "boat") {
      return pkg?.speedBoatPrice?.[currency]?.adult || 0
    }
    else {
      return pkg?.price?.[currency]?.adult || 0
    }
  }

  return (
    <>
      <MetaData
        title={`Krabi Tour Packages${queryContext} | Be Alright Krabi`}
        description={`Explore dynamic Krabi tour packages${queryContext}. Find island tours, diving, kayaking, and family adventures around Krabi, Thailand.`}
        keywords={`Krabi tour packages, Krabi island tours, Krabi activities, Thailand travel packages, Ao Nang tours${queryLabel ? `, ${queryLabel}` : ''}`}
        canonicalUrl={canonicalUrl}
        ogTitle={`Krabi Tour Packages${queryContext} | Be Alright Krabi`}
        ogDescription={`Explore dynamic Krabi tour packages${queryContext}. Find island tours, diving, kayaking, and family adventures around Krabi, Thailand.`}
        ogImage={filteredPackages[0]?.img?.[0]}
        ogType='website'
        structuredData={packagesJsonLd}
        structuredDataId='packages-jsonld'
      />
      <div id='container-element' className='mx-auto my-4 w-[94%] max-w-7xl'>
        <h2 className='text-3xl font-black tracking-tight text-slate-900 sm:text-4xl'>{t('sliderMenu.tourPackages')}</h2>
        <nav className='mt-2' aria-label='breadcrumb'>
          <ol className='flex flex-wrap items-center gap-2 text-sm text-slate-500'>
            <li>
              <a href='/' className='font-medium text-slate-600 transition hover:text-[#1739bd]'>{t("sliderMenu.home")}</a>
            </li>
            <li className='text-slate-400'>
              <FiChevronRight />
            </li>
            <li className='font-semibold text-slate-700' aria-current='page'>{t("sliderMenu.tourPackages")}</li>
          </ol>
        </nav>

        <div className='mt-4 mb-4 flex items-center justify-between rounded-2xl border border-slate-200/80 bg-white/85 px-3 py-2 shadow-sm backdrop-blur-sm'>
          <p className='text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 sm:text-sm'>
            {queryLabel ? `${filteredPackages.length} results` : 'Refine your package search'}
          </p>
          <button
            className='inline-flex items-center gap-2 rounded-full border border-[#1739bd]/40 bg-white px-4 py-2 text-sm font-semibold text-[#1739bd] transition hover:-translate-y-[1px] hover:bg-[#1739bd]/5 disabled:cursor-not-allowed disabled:opacity-40'
            onClick={resetSearch}
            disabled={isFiltered()}
          >
            <FiRotateCcw />
            {t("packagePage.clear")}
          </button>
        </div>

        <SearchBlock onSuggestionSelected={scrollToCardBlock} />
        <div ref={cardsBlockRef} className='mt-4 grid grid-cols-1 justify-items-center gap-4 sm:grid-cols-2 xl:grid-cols-3'>
          {

            filteredPackages.map((packageItem, i) => {
              return (
                <article className='package-card w-full max-w-[380px]' key={i} onClick={() => navPage(`/package/${packageItem.path}`)}>
                  <img src={packageItem?.img?.[0] || ''} className="package-card-img" alt={packageItem?.title || t('packagePage.more')} />
                  <div className="p-2">
                    <h5 className="card-title my-2 line-clamp-1 text-[#1739bd] font-medium">{packageItem.title}</h5>
                    <p className="card-desc my-2">{packageItem.desc}</p>
                    <p className='card-price my-2 text-lg font-semibold'>
                      {currency === 'usd' && '$'}{currency === 'thb' && '฿'} {renderPrice(packageItem) || 0} {currency === 'usd' && t('packagePage.currency.usd')}{currency === 'thb' && t('packagePage.currency.thb')}
                    </p>
                    <div className='flex justify-between gap-2'>
                      <button className='sk-btn rounded-full' onClick={() => navPage(`/package/${packageItem.path}`)}>{t("packagePage.more")}</button>
                    </div>
                  </div>
                </article>
              )
            })
          }
        </div>
        {filteredPackages.length === 0 && (
          <div className='mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-medium text-slate-500'>
            {t('searchbox.noResult')}
          </div>
        )}
      </div>
    </>
  )
}

export default Packages
